/**
 * FeatureFlagsContext - Provides feature flag status throughout the app
 * Checks both global flags and user-specific overrides
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMyFeatures, checkFeature, FeatureStatus } from '../services/analyticsService';

interface FeatureFlagsContextType {
  features: Record<string, FeatureStatus>;
  loading: boolean;
  error: string | null;
  isFeatureEnabled: (featureKey: string) => boolean;
  refreshFeatures: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  features: {},
  loading: true,
  error: null,
  isFeatureEnabled: () => false,
  refreshFeatures: async () => {},
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
  userId: string | number | undefined;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children, userId }) => {
  const [features, setFeatures] = useState<Record<string, FeatureStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshFeatures = useCallback(async () => {
    if (!userId) {
      setFeatures({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const data = await getMyFeatures(userId);
      setFeatures(data);
    } catch (err: any) {
      console.error('Failed to fetch feature flags:', err);
      setError(err.message);
      // Set default features on error (all disabled)
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshFeatures();
  }, [refreshFeatures]);

  const isFeatureEnabled = useCallback((featureKey: string): boolean => {
    const feature = features[featureKey];
    if (!feature) return false;
    return feature.enabled;
  }, [features]);

  return (
    <FeatureFlagsContext.Provider
      value={{
        features,
        loading,
        error,
        isFeatureEnabled,
        refreshFeatures,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to check if a specific feature is enabled
 */
export const useFeature = (featureKey: string): { enabled: boolean; loading: boolean } => {
  const { features, loading } = useFeatureFlags();
  const feature = features[featureKey];
  return {
    enabled: feature?.enabled ?? false,
    loading,
  };
};

/**
 * Component that conditionally renders children based on feature flag
 */
interface FeatureGateProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, children, fallback = null }) => {
  const { enabled, loading } = useFeature(feature);
  
  if (loading) return null;
  if (!enabled) return <>{fallback}</>;
  return <>{children}</>;
};

export default FeatureFlagsContext;
